/**
 * Item identifier display helpers (issue #174).
 *
 * Two forms of an item id:
 *  - friendly: the human prefix id (REQ-0001), from requirement_id / human_id /
 *    display_id.
 *  - raw: the canonical UUID.
 *
 * The `showHumanIds` user pref decides which is rendered inline; the OTHER form is
 * what the advanced tooltip reveals (so a GUID is never shown inline when friendly
 * is selected, and vice-versa — per #174).
 */

export function friendlyId(item) {
  if (!item) return '';
  return String(item.requirement_id || item.human_id || item.display_id || '').trim();
}

export function rawId(item) {
  if (!item) return '';
  return String(item.id || item.item_uuid || '').trim();
}

/** Short, never-wrapping label for a raw UUID (REQ-NNNN passes through whole). */
export function shortId(id) {
  const raw = String(id || '');
  if (!raw) return '';
  const looksUuid = raw.length > 14 && /[0-9a-f]/i.test(raw) && !/^[A-Za-z]+-\d+$/.test(raw);
  return looksUuid ? `${raw.slice(0, 8)}…` : raw;
}

/**
 * Drop a friendly id's `PREFIX-` (e.g. "REQ-0042" -> "0042") for tight spaces
 * like the advanced tooltip header, where the type badge already conveys what
 * the prefix would have said. Anything that isn't a `PREFIX-NNN` shape (a
 * short/raw id) passes through unchanged.
 */
export function stripIdPrefix(id) {
  const raw = String(id || '');
  const m = raw.match(/^[A-Za-z]{1,8}-(.+)$/);
  return m ? m[1] : raw;
}

/**
 * Canonical `#item=<id>` hash-fragment URL for deep-linking to an item (issue
 * #339): the tree page (`useTreeState`) reads/writes exactly this hash to open
 * the branch to `id` without leaving `/`. Centralised here so every navigational
 * surface (sidebar, menus, chips, tree rows, ...) builds the SAME string instead
 * of re-deriving the `#item=` literal — previously duplicated ad hoc in
 * RequirementLink, ItemTooltip, CommandPalette, GlobalSearchBox and ActivityFeed.
 */
export function itemHashHref(id) {
  return id ? `#item=${id}` : undefined;
}

/**
 * Resolve what to show inline vs. in the tooltip for an item, honouring the pref.
 * @returns {{ primary: string, secondary: string|null, primaryIsRaw: boolean }}
 */
export function resolveIdDisplay(item, showHumanIds = true) {
  const friendly = friendlyId(item);
  const raw = rawId(item);
  if (showHumanIds && friendly) {
    // Inline the friendly id; the tooltip can reveal the raw UUID.
    return { primary: friendly, secondary: raw || null, primaryIsRaw: false };
  }
  // GUID mode (or no friendly id): inline the (shortened) UUID; reveal friendly.
  return { primary: shortId(raw) || friendly, secondary: friendly || raw || null, primaryIsRaw: true };
}
